import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft,
  ArrowLeftRight,
  CheckCircle2,
  CircleAlert,
  Download,
  FileText,
  HelpCircle,
  Loader2,
  RotateCw,
  Save,
  Search,
  TriangleAlert,
  XCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useInvoicesApi, useRegistryApi, useCompaniesApi, useTagsApi, useProjectsApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import type { InvoiceDetail as InvoiceDetailType, InvoiceDirection, UpdateInvoiceDto, CompanyListItem, Tag as TagType, ProjectOption } from '@ip/shared-types';
import { PAYMENT_METHODS } from '@ip/shared-types';
import { detectPaymentTypeFromAccount, validateSwiftBic } from '@/utils/iban';
import { formatPLN } from '@/utils/currency';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DatePicker } from '@/components/shared/DatePicker';
import { InvoiceLineItemsTable } from './InvoiceLineItemsTable';
import { InvoiceProjectPicker } from './InvoiceProjectPicker';
import { KsefMetadataPanel } from './KsefMetadataPanel';
import { ContractorSelector } from './ContractorSelector';
import { InvoiceTagsEditor } from './InvoiceTagsEditor';
import { InvoiceNotesPanel } from './InvoiceNotesPanel';
import { InvoicePaymentsCard } from '@/components/sales/InvoicePaymentsCard';
import { CombinedPaymentCard } from './CombinedPaymentCard';
import { EXPORT_ISSUE_LABELS, TERMINAL_EXPORT_ISSUES } from '@/utils/exportIssueLabels';
import { sanitizeFileNamePart } from '@/utils/download';
import { t } from '@/i18n';

const PAYMENT_TYPE_BADGES: Record<string, string> = {
  domestic: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  sepa: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  swift: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
};
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  domestic: 'Domestic', sepa: 'SEPA', swift: 'SWIFT',
};

const ALERT_WARNING_CLASS = 'border-amber-500/50 text-amber-800 dark:text-amber-300 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400';
const ALERT_ERROR_CLASS = 'border-destructive/50';

interface FormValues {
  invoiceNumber: string | null;
  vendorName: string | null;
  vendorNip: string | null;
  vendorStreet: string | null;
  vendorPostalCode: string | null;
  vendorCity: string | null;
  vendorCountry: string | null;
  gross: number | null;
  net: number | null;
  tax: number | null;
  currency: string;
  invoiceIssueDate: Date | null;
  dateOfSale: Date | null;
  paymentDate: Date | null;
  hasSplitPayment: boolean;
  bankAccount: string | null;
  swiftBic: string | null;
  paymentMethod: string | null;
  status: string;
  isByCar: boolean;
  tripDestination: string | null;
}

interface InvoiceDetailProps {
  companyNip: string;
  companyName: string;
  invoiceId: string;
  // Receives the loaded invoice's direction (undefined if not yet loaded) so
  // callers can navigate back to the matching list (Sprzedaż vs Koszty).
  onClose: (direction?: InvoiceDirection) => void;
  onSaved?: (direction?: InvoiceDirection) => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      {children}
    </div>
  );
}

export function InvoiceDetail({ companyNip, companyName, invoiceId, onClose, onSaved }: InvoiceDetailProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingBir, setFetchingBir] = useState(false);
  const [recheckingVat, setRecheckingVat] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceDetailType | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [linkedContractorId, setLinkedContractorId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [changeCompanyOpen, setChangeCompanyOpen] = useState(false);
  // Close/back with unsaved edits pops a discard confirm instead of losing them.
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [targetCompanyNip, setTargetCompanyNip] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [values, setValues] = useState<FormValues | null>(null);
  const [availableTags, setAvailableTags] = useState<TagType[]>([]);
  // Task B3: projects for the whole-invoice + per-line selectors. Stays empty
  // (selectors hidden) when the list call is denied — e.g. tag-restricted
  // users, for whom the backend refuses the aggregate project list.
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  // Task B3: OCR/upload cost invoices now carry line items too — the document
  // panel becomes a "PDF | Pozycje" toggle for them, PDF stays the default.
  const [docTab, setDocTab] = useState<'pdf' | 'lines'>('pdf');

  const { getInvoice, updateInvoice, updateInvoiceTags, recheckVat, getKsefPdf, downloadInvoicePdf, assignInvoices } = useInvoicesApi();
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [originalDownloading, setOriginalDownloading] = useState(false);
  const { lookupRegistry } = useRegistryApi();
  const { getCompanies } = useCompaniesApi();
  const { getTags } = useTagsApi();
  const { listProjectOptions, assignInvoiceProject, assignLineItemProjects } = useProjectsApi();
  const { isSuperAdmin, permissions } = useAuth();

  // Payment-delete and note-edit/delete are admin/ksiegowy-grade actions —
  // the backend enforces this (checkCompanyRole); the UI just hides the
  // buttons for everyone else. hasCompanyRole already treats superadmin as
  // true, but legacy Entra-group admins have permissions === null, so OR in
  // isSuperAdmin explicitly.
  const canManageRecords = isSuperAdmin || hasCompanyRole(permissions, companyNip, ['admin', 'ksiegowy']);

  // Guarded close: prompt before discarding unsaved edits, otherwise leave.
  const attemptClose = useCallback(() => {
    if (hasChanges) {
      setConfirmCloseOpen(true);
      return;
    }
    onClose(invoice?.direction);
  }, [hasChanges, onClose, invoice?.direction]);

  const setField = useCallback(<K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((prev) => (prev ? { ...prev, [key]: value } : prev));
    setHasChanges(true);
  }, []);

  // Reactive payment type detection
  const bankAccount = values?.bankAccount;
  const currency = values?.currency;
  const vendorCountry = values?.vendorCountry;
  const vendorCity = values?.vendorCity;
  const hasSplitPayment = values?.hasSplitPayment;

  const paymentTypeInfo = useMemo(() => {
    if (!bankAccount || bankAccount.replace(/\s/g, '').length < 6) return null;
    return detectPaymentTypeFromAccount(bankAccount, currency || 'PLN', vendorCountry);
  }, [bankAccount, currency, vendorCountry]);

  const loadInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getInvoice(companyNip, invoiceId);
      setInvoice(data);
      setLinkedContractorId(data.contractorId ?? null);

      setValues({
        invoiceNumber: data.invoiceNumber,
        vendorName: data.contractorName,
        vendorNip: data.contractorNip,
        vendorStreet: data.vendorStreet || data.vendorAddress,
        vendorPostalCode: data.vendorPostalCode,
        vendorCity: data.vendorCity,
        vendorCountry: data.vendorCountry,
        gross: data.amount,
        net: data.netAmount,
        tax: data.taxAmount,
        currency: data.currency || 'PLN',
        invoiceIssueDate: data.issueDate ? new Date(data.issueDate) : null,
        dateOfSale: data.saleDate ? new Date(data.saleDate) : null,
        paymentDate: data.paymentDate ? new Date(data.paymentDate) : null,
        hasSplitPayment: data.hasSplitPayment ?? false,
        bankAccount: data.bankAccount,
        swiftBic: data.swiftBic ?? null,
        paymentMethod: data.paymentMethod,
        status: data.status,
        isByCar: data.isByCar ?? false,
        tripDestination: data.tripDestination ?? null,
      });
    } catch (error) {
      toast.error(t('invoices.detail.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [companyNip, invoiceId, getInvoice]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  // Light refresh after payment add/delete: re-fetch the invoice to pick up
  // the derived settlement fields + payment list WITHOUT resetting the form
  // (values/hasChanges stay untouched — a full loadInvoice would silently
  // discard unsaved edits and flash the whole-page spinner).
  const refreshInvoice = useCallback(async () => {
    try {
      const data = await getInvoice(companyNip, invoiceId);
      setInvoice(data);
    } catch (error) {
      console.error('Failed to refresh invoice:', error);
    }
  }, [companyNip, invoiceId, getInvoice]);

  // Tag module follows the invoice direction ('sales' tags for sale
  // invoices, 'expense' for purchases) — so wait for the invoice to load.
  const invoiceDirection = invoice?.direction;
  useEffect(() => {
    if (!invoiceDirection) return;
    let mounted = true;
    getTags(companyNip, invoiceDirection === 'sale' ? 'sales' : 'expense')
      .then((data) => {
        if (mounted) setAvailableTags(data);
      })
      .catch((error) => console.error('Failed to load tags:', error));
    return () => {
      mounted = false;
    };
  }, [companyNip, invoiceDirection, getTags]);

  // Tags save immediately (same as the list pickers) — independent of the
  // form's Save button and its hasChanges tracking.
  const handleTagToggle = useCallback(
    async (tagId: string, checked: boolean) => {
      if (!invoice) return;
      const newTags = checked
        ? [...invoice.tags, tagId]
        : invoice.tags.filter((id) => id !== tagId);
      const previousTags = invoice.tags;
      setInvoice((prev) => (prev ? { ...prev, tags: newTags } : prev));
      try {
        await updateInvoiceTags(companyNip, invoice.id, newTags);
      } catch (error) {
        setInvoice((prev) => (prev ? { ...prev, tags: previousTags } : prev));
        toast.error(t('invoices.tagsEditor.updateError'));
        console.error(error);
      }
    },
    [invoice, companyNip, updateInvoiceTags],
  );

  // Task B3: load the company's projects for the assignment selectors. A 403
  // (tag-restricted user) or any failure just leaves the selectors hidden.
  useEffect(() => {
    let mounted = true;
    listProjectOptions(companyNip)
      .then((data) => {
        if (mounted) setProjects(data);
      })
      .catch(() => {
        /* no access to project list — hide the project selectors */
      });
    return () => {
      mounted = false;
    };
  }, [companyNip, listProjectOptions]);

  // Residual rule: assigned lines count to their own projects, everything
  // else inherits the invoice's project. The flag only drives the "częściowo"
  // badge next to the whole-invoice picker (which stays usable).
  const hasLineAssignments = useMemo(
    () =>
      invoice?.lineItems?.some((li) => li.projectId != null) ??
      invoice?.hasLineProjects ??
      false,
    [invoice],
  );

  // The invoice's own project — unassigned lines inherit it, so the line
  // selects show "Dziedziczy: <name>" instead of "Brak".
  const inheritedProject = useMemo(() => {
    if (!invoice?.projectId) return null;
    const project = projects.find((p) => p.id === invoice.projectId);
    const name = project?.name ?? invoice.projectName;
    return name ? { name, color: project?.color ?? null } : null;
  }, [invoice, projects]);

  // Whole-invoice assignment: optimistic update, revert + toast on failure.
  // A user-made change is always MANUAL provenance (the backend stamps it the
  // same way), so the optimistic state also drops any "auto" rule hint.
  const handleInvoiceProjectChange = useCallback(
    async (projectId: string | null) => {
      if (!invoice) return;
      const previous = {
        projectId: invoice.projectId ?? null,
        projectName: invoice.projectName ?? null,
        projectAssignedBy: invoice.projectAssignedBy ?? null,
      };
      const projectName = projectId
        ? (projects.find((p) => p.id === projectId)?.name ?? null)
        : null;
      setInvoice((prev) =>
        prev
          ? { ...prev, projectId, projectName, projectAssignedBy: projectId ? 'manual' : null }
          : prev,
      );
      try {
        await assignInvoiceProject(companyNip, invoiceId, projectId);
      } catch (error) {
        setInvoice((prev) => (prev ? { ...prev, ...previous } : prev));
        toast.error(t('projects.selector.assignError'));
        console.error(error);
      }
    },
    [invoice, projects, companyNip, invoiceId, assignInvoiceProject],
  );

  // Per-line assignment (bulk endpoint, single-line payload): optimistic
  // update of the line, revert + toast on failure.
  const handleLineProjectChange = useCallback(
    async (lineItemId: string, projectId: string | null) => {
      if (!invoice?.lineItems) return;
      const previousLines = invoice.lineItems;
      setInvoice((prev) =>
        prev?.lineItems
          ? {
              ...prev,
              lineItems: prev.lineItems.map((li) =>
                li.id === lineItemId ? { ...li, projectId } : li,
              ),
            }
          : prev,
      );
      try {
        await assignLineItemProjects(companyNip, invoiceId, [{ lineItemId, projectId }]);
      } catch (error) {
        setInvoice((prev) => (prev ? { ...prev, lineItems: previousLines } : prev));
        toast.error(t('projects.selector.assignError'));
        console.error(error);
      }
    },
    [invoice, companyNip, invoiceId, assignLineItemProjects],
  );

  useEffect(() => {
    async function loadCompanies() {
      try {
        const data = await getCompanies();
        setCompanies(data.filter((c) => c.nip !== 'other' && c.nip !== companyNip));
      } catch (error) {
        console.error('Failed to load companies:', error);
      }
    }
    loadCompanies();
  }, [companyNip, getCompanies]);

  const handleSave = async () => {
    if (!values) return;
    // Catch a malformed BIC here rather than letting the backend validator
    // answer with a bare 400 — the field is right there and the message says
    // what is wrong with it.
    if (values.swiftBic && !validateSwiftBic(values.swiftBic).valid) {
      toast.error(validateSwiftBic(values.swiftBic).error || 'Invalid SWIFT/BIC');
      return;
    }
    try {
      setSaving(true);

      const updateData: UpdateInvoiceDto = {
        invoiceNumber: values.invoiceNumber,
        vendorName: values.vendorName,
        vendorNip: values.vendorNip,
        vendorStreet: values.vendorStreet,
        vendorPostalCode: values.vendorPostalCode,
        vendorCity: values.vendorCity,
        vendorCountry: values.vendorCountry,
        gross: values.gross,
        net: values.net,
        tax: values.tax,
        currency: values.currency,
        invoiceIssueDate: values.invoiceIssueDate ? dayjs(values.invoiceIssueDate).format('YYYY-MM-DD') : null,
        dateOfSale: values.dateOfSale ? dayjs(values.dateOfSale).format('YYYY-MM-DD') : null,
        paymentDate: values.paymentDate ? dayjs(values.paymentDate).format('YYYY-MM-DD') : null,
        hasSplitPayment: values.hasSplitPayment,
        bankAccount: values.bankAccount,
        swiftBic: values.swiftBic,
        paymentMethod: values.paymentMethod,
        contractorId: linkedContractorId,
        status: values.status as UpdateInvoiceDto['status'],
        isByCar: values.isByCar,
        tripDestination: values.isByCar ? values.tripDestination : null,
      };

      await updateInvoice(companyNip, invoiceId, updateData);
      toast.success(t('invoices.detail.saveSuccess'));
      setHasChanges(false);
      onSaved?.(invoice?.direction);
    } catch (error) {
      toast.error(t('invoices.detail.saveError'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleFetchFromRegistry = async () => {
    const nip = values?.vendorNip;
    if (!nip) {
      toast.warning(t('registry.refreshNoId'));
      return;
    }

    try {
      setFetchingBir(true);
      const data = await lookupRegistry(nip.trim());

      if (!data.name && !data.street && !data.city) {
        toast.warning(t('registry.lookupNotFound'));
        return;
      }

      // Only overwrite fields the registry actually filled — a partial answer
      // must not blank seller fields the user already has (data.X || prev.X).
      setValues((prev) =>
        prev
          ? {
              ...prev,
              vendorName: data.name || prev.vendorName,
              vendorStreet: data.street || prev.vendorStreet,
              vendorPostalCode: data.postalCode || prev.vendorPostalCode,
              vendorCity: data.city || prev.vendorCity,
              vendorCountry: data.country || prev.vendorCountry,
            }
          : prev,
      );
      setHasChanges(true);
      toast.success(t('registry.lookupSuccess'));
    } catch (error) {
      const status = (error as { status?: number })?.status;
      toast.error(status === 404 ? t('registry.lookupNotFound') : t('registry.lookupFailed'));
    } finally {
      setFetchingBir(false);
    }
  };

  const handleRecheckVat = async () => {
    try {
      setRecheckingVat(true);
      const vatData = await recheckVat(companyNip, invoiceId);
      setInvoice((prev) => (prev ? { ...prev, vatPayerData: vatData } : prev));
      toast.success(t('invoices.detail.vatRecheckSuccess'));
    } catch (error) {
      toast.error(t('invoices.detail.vatRecheckError'));
      console.error(error);
    } finally {
      setRecheckingVat(false);
    }
  };

  const handleAssign = async () => {
    if (!targetCompanyNip) return;
    try {
      setAssigning(true);
      await assignInvoices(companyNip, [invoiceId], targetCompanyNip);
      toast.success(t('invoices.assign.success'));
      onSaved?.(invoice?.direction);
      onClose(invoice?.direction);
    } catch (error) {
      toast.error(t('invoices.assign.error'));
      console.error(error);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-background flex h-screen items-center justify-center">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    );
  }

  if (!invoice || !values) {
    return (
      <div className="bg-background flex h-screen flex-col items-center justify-center gap-4 p-10 text-center">
        <p className="text-muted-foreground">Invoice not found</p>
        <Button variant="outline" onClick={() => onClose()}>Go Back</Button>
      </div>
    );
  }

  const pdfViewer = (
    <div className="flex flex-1 justify-center overflow-auto bg-[var(--color-pdf-viewer-bg)] p-4">
      {invoice.pdfUrl ? (
        <iframe
          src={`${invoice.pdfUrl}#navpanes=0&view=FitH`}
          className="bg-background h-full w-full border-0 shadow-md"
          title="Invoice PDF"
        />
      ) : (
        <div className="p-10 text-center">
          <p className="text-white">PDF not available</p>
        </div>
      )}
    </div>
  );

  // Task B3: non-KSeF invoices with extracted line items (OCR, Task B1) get a
  // "PDF | Pozycje" toggle so lines can be viewed and assigned to projects
  // without leaving the page. KSeF invoices keep their lines-first layout.
  const isKsefWithLines = invoice.source === 'ksef' && Boolean(invoice.lineItems?.length);
  const showDocTabs = !isKsefWithLines && Boolean(invoice.lineItems?.length);

  return (
    <div className="bg-background flex h-screen flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" aria-label="Go back" onClick={attemptClose}>
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-lg leading-tight font-semibold">
                {invoice.invoiceNumber || 'Invoice Details'}
              </h1>
              {/* Task A3: tags visible + editable right in the header (both directions) */}
              <InvoiceTagsEditor
                tags={availableTags}
                selectedTagIds={invoice.tags}
                onToggleTag={handleTagToggle}
              />
              {/* Whole-invoice project — same immediate-save pattern as the
                  tags; locked with a hint while line items carry their own
                  assignments (those win in attribution). */}
              {projects.length > 0 && (
                <InvoiceProjectPicker
                  projects={projects}
                  projectId={invoice.projectId ?? null}
                  projectName={invoice.projectName ?? null}
                  assignedBy={invoice.projectAssignedBy ?? null}
                  hasLineProjects={hasLineAssignments}
                  onChange={handleInvoiceProjectChange}
                />
              )}
            </div>
            <p className="text-muted-foreground text-sm">Preview and edit invoice</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-sm">{companyName}</span>
          {companyNip !== 'other' && companies.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('invoices.assign.changeCompany')}
                  onClick={() => setChangeCompanyOpen((o) => !o)}
                >
                  <ArrowLeftRight className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('invoices.assign.changeCompany')}</TooltipContent>
            </Tooltip>
          )}
          <Button variant="outline" onClick={attemptClose}>Close</Button>
          <Button onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Save changes
          </Button>
        </div>
      </div>

      {/* Assign to Company: full banner ONLY for unassigned invoices;
          assigned ones get the compact header toggle instead */}
      {companies.length > 0 && (companyNip === 'other' || changeCompanyOpen) && (
        <Alert className="rounded-none border-x-0 border-t-0">
          <ArrowLeftRight className="size-4" />
          <AlertTitle>
            {companyNip === 'other'
              ? t('invoices.assign.sectionTitle')
              : t('invoices.assign.changeCompanyTitle')}
          </AlertTitle>
          <AlertDescription>
            <div className="mt-2 flex items-center gap-2">
              <Select
                value={targetCompanyNip ?? ''}
                onValueChange={(v) => setTargetCompanyNip(v || null)}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder={t('invoices.bulk.selectCompany')} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.nip} value={c.nip}>
                      {c.name} ({c.nip})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handleAssign}
                disabled={!targetCompanyNip || assigning}
              >
                {assigning && <Loader2 className="size-4 animate-spin" />}
                {t('invoices.assign.button')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* PDF Viewer - Left Column */}
        <div className="flex w-[55%] flex-col border-r">
          {showDocTabs && (
            <div className="border-b px-4 py-2">
              <Tabs value={docTab} onValueChange={(v) => setDocTab(v as 'pdf' | 'lines')}>
                <TabsList>
                  <TabsTrigger value="pdf">{t('invoices.detail.tabPdf')}</TabsTrigger>
                  <TabsTrigger value="lines">{t('invoices.detail.tabLineItems')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}
          {isKsefWithLines && invoice.lineItems?.length ? (
            <>
              <div className="min-h-0 flex-1 overflow-auto">
                <div className="flex min-h-0 w-full flex-col">
                  <InvoiceLineItemsTable
                    lineItems={invoice.lineItems}
                    currency={invoice.currency}
                    projects={projects}
                    onLineProjectChange={handleLineProjectChange}
                    inheritedProject={inheritedProject}
                  />
                  <KsefMetadataPanel
                    ksefNumber={invoice.ksefNumber}
                    ksefVerificationUrl={invoice.ksefVerificationUrl}
                    ksefMetadata={invoice.ksefMetadata}
                    customerName={invoice.customerName}
                    customerNip={invoice.customerNip}
                    companyNip={companyNip}
                    correctedInvoices={invoice.correctedInvoices}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 border-t px-4 py-3">
                <Button
                  variant="outline"
                  disabled={pdfDownloading}
                  onClick={async () => {
                    setPdfDownloading(true);
                    try {
                      const blob = await getKsefPdf(companyNip, invoiceId);
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `FV-${invoice.invoiceNumber || invoiceId}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    } catch {
                      toast.error(t('invoices.detail.downloadPdfError'));
                    } finally {
                      setPdfDownloading(false);
                    }
                  }}
                >
                  {pdfDownloading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <FileText className="size-4" />
                  )}
                  Pobierz PDF
                </Button>
                {invoice.pdfUrl && (
                  <Button
                    variant="outline"
                    disabled={originalDownloading}
                    onClick={async () => {
                      setOriginalDownloading(true);
                      try {
                        // Streams the original through our API and saves it —
                        // failures toast inside downloadInvoicePdf.
                        await downloadInvoicePdf(
                          companyNip,
                          invoiceId,
                          invoice.fileName ||
                            `FV-${sanitizeFileNamePart(invoice.invoiceNumber || invoiceId)}.pdf`,
                        );
                      } catch {
                        /* session expired — handled by the global modal */
                      } finally {
                        setOriginalDownloading(false);
                      }
                    }}
                  >
                    {originalDownloading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Download className="size-4" />
                    )}
                    Pobierz oryginał
                  </Button>
                )}
              </div>
            </>
          ) : showDocTabs && docTab === 'lines' && invoice.lineItems?.length ? (
            <div className="min-h-0 flex-1 overflow-auto">
              <div className="flex min-h-0 w-full flex-col">
                <InvoiceLineItemsTable
                  lineItems={invoice.lineItems}
                  currency={invoice.currency}
                  projects={projects}
                  onLineProjectChange={handleLineProjectChange}
                  inheritedProject={inheritedProject}
                />
              </div>
            </div>
          ) : (
            pdfViewer
          )}
        </div>

        {/* Form - Right Column */}
        <div className="bg-muted/30 w-[45%] space-y-4 overflow-auto p-4">
          {/* Accounting notes (Task A3) — top of the column, above the data
              cards, so they're seen before the line items / amounts */}
          <InvoiceNotesPanel
            companyNip={companyNip}
            invoiceId={invoiceId}
            canManage={canManageRecords}
          />

          {/* Payment history (Task A3) — sale invoices only */}
          {invoice.direction === 'sale' && (
            <InvoicePaymentsCard
              companyNip={companyNip}
              invoice={invoice}
              canManage={canManageRecords}
              onChanged={refreshInvoice}
            />
          )}

          {/* Combined payment (płatność łączona) — purchase invoices paid as
              part of one multi-invoice bank/card transaction */}
          {invoice.combinedPayment && (
            <CombinedPaymentCard
              companyNip={companyNip}
              invoiceId={invoiceId}
              direction={invoice.direction === 'sale' ? 'sale' : 'purchase'}
              payment={invoice.combinedPayment}
              canManage={canManageRecords}
              onChanged={refreshInvoice}
            />
          )}

          {/* Basic Information Card */}
          <Card className="gap-3 py-4">
            <CardHeader className="px-4">
              <CardTitle className="text-sm font-medium">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4">
              <Field label="Invoice Number">
                <Input
                  placeholder={t('invoices.detail.invoiceNumberPlaceholder')}
                  value={values.invoiceNumber ?? ''}
                  onChange={(e) => setField('invoiceNumber', e.target.value)}
                />
              </Field>

              {invoice.source === 'ksef' && invoice.ksefNumber && (
                <Field label="KSeF Number">
                  <Input value={invoice.ksefNumber} disabled />
                </Field>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Issue Date">
                  <DatePicker
                    value={values.invoiceIssueDate}
                    onChange={(date) => setField('invoiceIssueDate', date)}
                  />
                </Field>
                <Field label="Sale Date">
                  <DatePicker
                    value={values.dateOfSale}
                    onChange={(date) => setField('dateOfSale', date)}
                  />
                </Field>
              </div>

              {/* Task B3 note: the whole-invoice project selector moved to
                  the header (InvoiceProjectPicker, next to the tags) — a
                  second control here would fight over the same field. */}
            </CardContent>
          </Card>

          {/* Amounts Card */}
          <Card className="gap-3 py-4">
            <CardHeader className="px-4">
              <CardTitle className="text-sm font-medium">Amounts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Net Amount">
                  <Input
                    type="number"
                    step="0.01"
                    value={values.net ?? ''}
                    onChange={(e) => setField('net', e.target.value === '' ? null : Number(e.target.value))}
                  />
                </Field>
                <Field label="Tax Amount">
                  <Input
                    type="number"
                    step="0.01"
                    value={values.tax ?? ''}
                    onChange={(e) => setField('tax', e.target.value === '' ? null : Number(e.target.value))}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Gross Amount">
                  <Input
                    type="number"
                    step="0.01"
                    value={values.gross ?? ''}
                    onChange={(e) => setField('gross', e.target.value === '' ? null : Number(e.target.value))}
                  />
                </Field>
                <Field label="Currency">
                  <Select value={values.currency} onValueChange={(v) => setField('currency', v)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PLN">PLN</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      {values.currency &&
                        !['PLN', 'EUR', 'USD', 'GBP'].includes(values.currency) && (
                          <SelectItem value={values.currency}>{values.currency}</SelectItem>
                        )}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {invoice.currency && invoice.currency !== 'PLN' && (
                <p className="text-muted-foreground text-xs">
                  {invoice.fxRate != null && invoice.grossPln != null
                    ? t('fx.plnEquivalent', {
                        amount: formatPLN(invoice.grossPln),
                        rate: invoice.fxRate.toLocaleString('pl-PL', {
                          minimumFractionDigits: 4,
                          maximumFractionDigits: 4,
                        }),
                        date: invoice.fxRateDate ?? '',
                      })
                    : t('fx.noRate')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Seller Card */}
          <Card className="gap-3 py-4">
            <CardHeader className="px-4">
              <CardTitle className="text-sm font-medium">Seller</CardTitle>
              <CardAction className="flex items-center gap-2">
                <ContractorSelector
                  companyNip={companyNip}
                  value={linkedContractorId}
                  onChange={(contractorId, contractor) => {
                    setLinkedContractorId(contractorId);
                    setHasChanges(true);
                    if (contractor) {
                      setValues((prev) =>
                        prev
                          ? {
                              ...prev,
                              vendorName: contractor.name,
                              vendorNip: contractor.nip,
                              vendorStreet: contractor.street,
                              vendorPostalCode: contractor.postalCode,
                              vendorCity: contractor.city,
                              vendorCountry: contractor.country,
                              bankAccount:
                                contractor.structuredBankAccounts?.[0]?.iban ||
                                contractor.bankAccounts?.[0] ||
                                prev.bankAccount,
                            }
                          : prev,
                      );
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFetchFromRegistry}
                  disabled={fetchingBir}
                >
                  {fetchingBir ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Search className="size-4" />
                  )}
                  {t('registry.lookup')}
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent className="space-y-3 px-4">
              <Field label="Name">
                <Input
                  placeholder={t('invoices.detail.sellerNamePlaceholder')}
                  value={values.vendorName ?? ''}
                  onChange={(e) => setField('vendorName', e.target.value)}
                />
              </Field>
              <Field label="NIP">
                <Input
                  placeholder={t('invoices.detail.taxIdPlaceholder')}
                  value={values.vendorNip ?? ''}
                  onChange={(e) => setField('vendorNip', e.target.value)}
                />
              </Field>
              <Field label="Street">
                <Input
                  placeholder="Street address"
                  value={values.vendorStreet ?? ''}
                  onChange={(e) => setField('vendorStreet', e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-[1fr_1.4fr_0.8fr] gap-2">
                <Field label="Postal">
                  <Input
                    placeholder="00-000"
                    value={values.vendorPostalCode ?? ''}
                    onChange={(e) => setField('vendorPostalCode', e.target.value)}
                  />
                </Field>
                <Field label="City">
                  <Input
                    placeholder={t('invoices.detail.cityPlaceholder')}
                    value={values.vendorCity ?? ''}
                    onChange={(e) => setField('vendorCity', e.target.value)}
                  />
                </Field>
                <Field label="Country">
                  <Input
                    placeholder="PL"
                    value={values.vendorCountry ?? ''}
                    onChange={(e) => setField('vendorCountry', e.target.value)}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* Payment Card */}
          <Card className="gap-3 py-4">
            <CardHeader className="px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <span>Payment</span>
                {paymentTypeInfo?.type && (
                  <Badge variant="outline" className={PAYMENT_TYPE_BADGES[paymentTypeInfo.type]}>
                    {PAYMENT_TYPE_LABELS[paymentTypeInfo.type]}
                  </Badge>
                )}
                {paymentTypeInfo && !paymentTypeInfo.type && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400"
                      >
                        Unknown
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      Cannot determine payment type — check bank account and vendor country
                    </TooltipContent>
                  </Tooltip>
                )}
                {paymentTypeInfo && !paymentTypeInfo.isIban && paymentTypeInfo.type === 'swift' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                      >
                        Non-IBAN
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      Account is not a valid IBAN — non-IBAN account for SWIFT transfer
                    </TooltipContent>
                  </Tooltip>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4">
              <Field label="Payment Date">
                <DatePicker
                  value={values.paymentDate}
                  onChange={(date) => setField('paymentDate', date)}
                />
              </Field>
              <Field label="Payment Method">
                <Select
                  value={values.paymentMethod ?? ''}
                  onValueChange={(v) => setField('paymentMethod', v === '__none__' ? null : v)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                    {values.paymentMethod &&
                      !PAYMENT_METHODS.some((m) => m.value === values.paymentMethod) && (
                        <SelectItem value={values.paymentMethod}>{values.paymentMethod}</SelectItem>
                      )}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Bank Account">
                <Input
                  placeholder="Account number"
                  value={values.bankAccount ?? ''}
                  onChange={(e) => setField('bankAccount', e.target.value)}
                />
              </Field>
              {invoice.vatPayerData?.accountAssigned === false && (
                <Alert variant="destructive">
                  <CircleAlert className="size-4" />
                  <AlertTitle className="line-clamp-none font-semibold">
                    {t('registry.whitelistWarning')}
                  </AlertTitle>
                  <AlertDescription>{t('registry.whitelistWarningDetail')}</AlertDescription>
                </Alert>
              )}
              {/* The BIC belongs HERE, on the invoice, not behind a hint pointing
                  at the contractor card: the vendor prints it on the document
                  and OCR already captured it, while a one-off non-IBAN payee may
                  have no card entry at all. It is required to assemble a SWIFT
                  transfer, so an empty value is flagged rather than silently
                  accepted. */}
              {paymentTypeInfo?.type === 'swift' && (
                <Field label="SWIFT / BIC">
                  <Input
                    placeholder="e.g. NOSCCATT"
                    value={values.swiftBic ?? ''}
                    onChange={(e) =>
                      setField('swiftBic', e.target.value.toUpperCase().trim() || null)
                    }
                    aria-invalid={!!values.swiftBic && !validateSwiftBic(values.swiftBic).valid}
                  />
                  {values.swiftBic && !validateSwiftBic(values.swiftBic).valid ? (
                    <p className="text-destructive mt-1 text-xs">
                      {validateSwiftBic(values.swiftBic).error}
                    </p>
                  ) : !values.swiftBic ? (
                    <p className="text-muted-foreground mt-1 text-xs">
                      {t('invoices.export.issues.swift_needs_bic')}
                    </p>
                  ) : null}
                </Field>
              )}
              {paymentTypeInfo?.type && !vendorCity && (
                <Alert className={ALERT_WARNING_CLASS}>
                  <TriangleAlert className="size-4" />
                  <AlertTitle className="line-clamp-none">
                    Vendor city is missing (required for valid export)
                  </AlertTitle>
                </Alert>
              )}
              {paymentTypeInfo?.type && paymentTypeInfo.type !== 'domestic' && !vendorCountry && (
                <Alert className={ALERT_WARNING_CLASS}>
                  <TriangleAlert className="size-4" />
                  <AlertTitle className="line-clamp-none">
                    International transfer requires vendor country (Seller section)
                  </AlertTitle>
                </Alert>
              )}
              {paymentTypeInfo?.type && paymentTypeInfo.type !== 'domestic' && hasSplitPayment === true && (
                <Alert variant="destructive" className={ALERT_ERROR_CLASS}>
                  <CircleAlert className="size-4" />
                  <AlertTitle className="line-clamp-none">
                    MPP (Split Payment) is not supported for international transfers
                  </AlertTitle>
                </Alert>
              )}
              {paymentTypeInfo && !paymentTypeInfo.isIban && !paymentTypeInfo.type && (
                <Alert variant="destructive" className={ALERT_ERROR_CLASS}>
                  <CircleAlert className="size-4" />
                  <AlertTitle className="line-clamp-none">
                    Invalid bank account — not a valid IBAN and vendor country is missing or PL
                  </AlertTitle>
                </Alert>
              )}
              {invoice.exportIssues && invoice.exportIssues.length > 0 && (() => {
                const actionable = invoice.exportIssues.filter((i) => !TERMINAL_EXPORT_ISSUES.includes(i));
                if (!actionable.length) return null;
                return (
                  <Alert className={ALERT_WARNING_CLASS}>
                    <TriangleAlert className="size-4" />
                    <AlertTitle>Export issues</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc pl-5">
                        {actionable.map((code) => (
                          <li key={code}>{EXPORT_ISSUE_LABELS[code] || code}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                );
              })()}
              <Field label="Split Payment (MPP)">
                <div>
                  <Switch
                    checked={values.hasSplitPayment}
                    onCheckedChange={(checked) => setField('hasSplitPayment', checked)}
                  />
                </div>
              </Field>
              <Field label="Payment Status">
                <Select value={values.status} onValueChange={(v) => setField('status', v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">{t('invoices.status.unpaid')}</SelectItem>
                    <SelectItem value="paid">{t('invoices.status.paid')}</SelectItem>
                    {/* 'exported' is a system state set by mBank export/bulk —
                        shown only when already exported so the control never
                        renders blank, not offered as a manual choice. */}
                    {values.status === 'exported' && (
                      <SelectItem value="exported">{t('invoices.status.exported')}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="By Car (Mileage)">
                <div>
                  <Switch
                    checked={values.isByCar}
                    onCheckedChange={(checked) => setField('isByCar', checked)}
                  />
                </div>
              </Field>
              {values.isByCar && (
                <Field label="Trip Destination">
                  <Input
                    placeholder="Full address for mileage trip"
                    value={values.tripDestination ?? ''}
                    onChange={(e) => setField('tripDestination', e.target.value)}
                  />
                </Field>
              )}
            </CardContent>
          </Card>

          {/* VAT Verification Card */}
          <Card className="gap-3 py-4">
            <CardHeader className="px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <span>VAT White List Verification</span>
                {invoice.vatPayerData?.accountAssigned === true && (
                  <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
                )}
                {invoice.vatPayerData?.accountAssigned === false && (
                  <XCircle className="size-4 text-red-600 dark:text-red-400" />
                )}
                {(invoice.vatPayerData?.accountAssigned === null || !invoice.vatPayerData) && (
                  <HelpCircle className="text-muted-foreground size-4" />
                )}
              </CardTitle>
              <CardAction>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRecheckVat}
                  disabled={recheckingVat}
                >
                  {recheckingVat ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RotateCw className="size-4" />
                  )}
                  Recheck
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent className="space-y-2 px-4 text-sm">
              <div>
                <span className="font-semibold">Account Status: </span>
                {invoice.vatPayerData?.accountAssigned === true && (
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="size-3.5" />
                    Account is on VAT White List
                  </span>
                )}
                {invoice.vatPayerData?.accountAssigned === false && (
                  <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                    <XCircle className="size-3.5" />
                    Account NOT on VAT White List
                  </span>
                )}
                {(invoice.vatPayerData?.accountAssigned === null || !invoice.vatPayerData) && (
                  <span className="text-muted-foreground inline-flex items-center gap-1">
                    <HelpCircle className="size-3.5" />
                    Not verified
                  </span>
                )}
              </div>
              {invoice.vatPayerData?.requestDateTime && invoice.vatPayerData.requestDateTime.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  Verified: {new Date(invoice.vatPayerData.requestDateTime).toLocaleString('pl-PL')}
                </p>
              )}
              {invoice.vatPayerData?.requestId && (
                <p className="text-muted-foreground text-xs">
                  Request ID: {invoice.vatPayerData.requestId}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.detail.discardConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('invoices.detail.discardConfirmMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                setConfirmCloseOpen(false);
                onClose(invoice.direction);
              }}
            >
              {t('invoices.detail.discardConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
